import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import PartnershipTypeTag from './PartnershipTypeTag';
import PartnershipStatusBadge from './PartnershipStatusBadge';
import TermsViewer from './TermsViewer';
import TermsEditor from './TermsEditor';
import MilestoneTracker from './MilestoneTracker';
import MilestoneModal from './MilestoneModal';

const {
  FiCheckCircle, FiChevronDown, FiChevronUp,
  FiPercent, FiFileText, FiEdit3, FiSave, FiX, FiTarget,
} = FiIcons;

const PartnershipCard = ({
  partnership, isOwner, onUpdateStatus,
  onToggleMilestone, onUpdateTerms, onAgreeToTerms,
  onAddMilestone, onUpdateMilestone, onDeleteMilestone,
  userId,
}) => {
  const [expandedTab, setExpandedTab] = useState(null);
  const [termsTab, setTermsTab]       = useState('view');
  const [editTerms, setEditTerms]     = useState(null);
  const [editRevSplit, setEditRevSplit] = useState(partnership.revenue_split || 50);
  const [savingTerms, setSavingTerms] = useState(false);
  const [milestoneModal, setMilestoneModal] = useState(null);

  const peer          = partnership._peer;
  const isInitiator   = partnership._role === 'initiator';
  const milestones    = partnership.milestones || [];
  const completedCount = milestones.filter(m => m.completed || m.status === 'done').length;
  const structuredTerms = partnership.terms_structured || {};
  const canManage     = isOwner && partnership.status === 'active';
  const overdueCount  = milestones.filter(m => !m.completed && m.status !== 'done' && m.target_date && new Date(m.target_date) < new Date()).length;

  if (!peer) return null;

  const toggleTab = (tab) => {
    setExpandedTab(prev => prev === tab ? null : tab);
    if (tab === 'terms') { setTermsTab('view'); setEditTerms(null); }
  };

  const startEditTerms = () => { setEditTerms({ ...structuredTerms }); setTermsTab('edit'); };
  const cancelEditTerms = () => { setEditTerms(null); setTermsTab('view'); };
  const saveTerms = async () => {
    setSavingTerms(true);
    await onUpdateTerms(partnership.id, editTerms, partnership.terms, editRevSplit);
    setSavingTerms(false); setTermsTab('view'); setEditTerms(null);
  };

  const handleMilestoneSave = async (milestoneId, form) => {
    if (milestoneId) return onUpdateMilestone(milestoneId, form);
    return onAddMilestone(partnership.id, form);
  };

  return (
    <div className="rounded-3xl overflow-hidden transition-all"
      style={{
        background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)',
        border: partnership.status === 'active' ? '1px solid rgba(255,102,204,0.15)' : '1px solid rgba(255,255,255,0.06)',
        backdropFilter: 'blur(20px)',
        boxShadow: partnership.status === 'active' ? 'inset 0 1px 0 rgba(255,255,255,0.08), 0 8px 32px rgba(0,0,0,0.3)' : 'none',
      }}>

      {(partnership.cover_image || peer.cover_image) && (
        <div className="h-20 relative overflow-hidden">
          <img src={partnership.cover_image || peer.cover_image} alt={partnership.title}
            className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 to-transparent" />
        </div>
      )}

      <div className="p-5 space-y-4">
        {/* Top row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to={`/${peer.slug}`} className="flex-shrink-0">
              <div className="w-11 h-11 rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
                {peer.avatar_image
                  ? <img src={peer.avatar_image} alt={peer.name} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-base font-black text-zinc-500">{peer.name?.[0]?.toUpperCase()}</div>
                }
              </div>
            </Link>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <Link to={`/${peer.slug}`} className="text-white font-black text-sm truncate hover:text-brand-pink transition-colors">{peer.name}</Link>
                {peer.verified && <SafeIcon icon={FiCheckCircle} className="text-blue-400 text-xs flex-shrink-0" />}
              </div>
              <p className="text-zinc-600 text-[9px] font-black uppercase tracking-widest">
                {isInitiator ? 'You initiated' : 'Invited you'}
              </p>
            </div>
          </div>
          <PartnershipStatusBadge status={partnership.status} />
        </div>

        {/* Title */}
        <div className="space-y-2">
          <h3 className="text-base font-black text-white uppercase tracking-tight leading-tight">{partnership.title}</h3>
          <div className="flex flex-wrap gap-2">
            <PartnershipTypeTag type={partnership.partnership_type} />
            {partnership.partnership_type === 'revenue_share' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-green-500/20 bg-green-500/5 text-green-400 text-[9px] font-black uppercase tracking-widest">
                <SafeIcon icon={FiPercent} className="text-[9px]" />
                {isInitiator ? partnership.revenue_split : 100 - partnership.revenue_split}% your cut
              </span>
            )}
          </div>
        </div>

        {/* Milestone progress */}
        {milestones.length > 0 && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-[9px] font-black uppercase tracking-widest">
              <span className="text-zinc-600">{completedCount}/{milestones.length} milestones</span>
              <div className="flex items-center gap-2">
                {overdueCount > 0 && <span className="text-red-400">{overdueCount} overdue</span>}
                <span className="text-zinc-600">{milestones.length > 0 ? Math.round((completedCount / milestones.length) * 100) : 0}%</span>
              </div>
            </div>
            <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700 bg-gradient-to-r from-brand-pink to-brand-coral"
                style={{ width: `${milestones.length ? (completedCount / milestones.length) * 100 : 0}%` }} />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-2 border-t border-white/5 gap-2 flex-wrap">
          <div className="flex gap-2 flex-wrap">
            {partnership.status === 'pending' && !isInitiator && isOwner && (
              <>
                <button onClick={() => onAgreeToTerms(partnership.id, userId)}
                  className="px-3 py-1.5 bg-green-500/10 border border-green-500/25 text-green-400 text-[9px] font-black uppercase tracking-widest rounded-xl hover:bg-green-500/20 transition-all">
                  Accept
                </button>
                <button onClick={() => onUpdateStatus(partnership.id, 'declined')}
                  className="px-3 py-1.5 bg-red-500/10 border border-red-500/25 text-red-400 text-[9px] font-black uppercase tracking-widest rounded-xl hover:bg-red-500/20 transition-all">
                  Decline
                </button>
              </>
            )}
            {partnership.status === 'active' && isOwner && isInitiator && (
              <button onClick={() => onUpdateStatus(partnership.id, 'ended')}
                className="px-3 py-1.5 border border-white/8 text-zinc-600 text-[9px] font-black uppercase tracking-widest rounded-xl hover:border-white/15 hover:text-zinc-400 transition-all">
                End
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button onClick={() => toggleTab('milestones')}
              className={`flex items-center gap-1 text-[9px] font-black uppercase tracking-widest transition-colors ${
                expandedTab === 'milestones' ? 'text-brand-pink' : 'text-zinc-600 hover:text-zinc-300'
              }`}>
              <SafeIcon icon={FiTarget} className="text-xs" />
              {milestones.length > 0 ? `${completedCount}/${milestones.length}` : 'Goals'}
              <SafeIcon icon={expandedTab === 'milestones' ? FiChevronUp : FiChevronDown} className="text-xs" />
            </button>
            <span className="text-zinc-800">|</span>
            <button onClick={() => toggleTab('terms')}
              className={`flex items-center gap-1 text-[9px] font-black uppercase tracking-widest transition-colors ${
                expandedTab === 'terms' ? 'text-brand-pink' : 'text-zinc-600 hover:text-zinc-300'
              }`}>
              <SafeIcon icon={FiFileText} className="text-xs" />
              Terms
              <SafeIcon icon={expandedTab === 'terms' ? FiChevronUp : FiChevronDown} className="text-xs" />
            </button>
          </div>
        </div>

        {/* Milestones expanded */}
        {expandedTab === 'milestones' && (
          <div className="pt-3 border-t border-white/5">
            <MilestoneTracker
              milestones={milestones}
              canManage={canManage}
              partnershipId={partnership.id}
              onToggle={onToggleMilestone}
              onAdd={() => setMilestoneModal({})}
              onEdit={(m) => setMilestoneModal(m)}
              onDelete={onDeleteMilestone}
              onUpdateMilestone={onUpdateMilestone}
            />
          </div>
        )}

        {/* Terms expanded */}
        {expandedTab === 'terms' && (
          <div className="space-y-3 pt-3 border-t border-white/5">
            <div className="flex items-center justify-between">
              <p className="text-[9px] font-black uppercase tracking-widest text-zinc-500">Partnership Terms</p>
              {isOwner && isInitiator && termsTab === 'view' && (
                <button onClick={startEditTerms}
                  className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-brand-pink hover:text-brand-coral transition-colors">
                  <SafeIcon icon={FiEdit3} className="text-xs" /> Edit
                </button>
              )}
              {termsTab === 'edit' && (
                <div className="flex items-center gap-2">
                  <button onClick={cancelEditTerms}
                    className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-zinc-500 hover:text-zinc-300 transition-colors">
                    <SafeIcon icon={FiX} className="text-xs" /> Cancel
                  </button>
                  <button onClick={saveTerms} disabled={savingTerms}
                    className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-green-400 hover:text-green-300 transition-colors disabled:opacity-50">
                    <SafeIcon icon={FiSave} className="text-xs" /> Save
                  </button>
                </div>
              )}
            </div>
            {termsTab === 'view' ? (
              <TermsViewer terms={structuredTerms} revSplit={partnership.revenue_split}
                isInitiator={isInitiator} agreedAt={partnership.terms_agreed_at} version={partnership.terms_version} />
            ) : (
              <TermsEditor terms={editTerms} onChange={setEditTerms}
                showRevSplit={partnership.partnership_type === 'revenue_share'}
                revSplit={editRevSplit} onRevSplitChange={setEditRevSplit} />
            )}
          </div>
        )}
      </div>

      {milestoneModal !== null && (
        <MilestoneModal
          milestone={milestoneModal?.id ? milestoneModal : null}
          onClose={() => setMilestoneModal(null)}
          onSave={handleMilestoneSave}
        />
      )}
    </div>
  );
};

export default PartnershipCard;